import React, { memo, useEffect, useState } from "react";
import styles from "./support-chat-modal.module.scss";
import caretDown from "@src/assets/svg/caret-down.svg";
import companyIcon from "@src/assets/svg/company-icon-gray.svg";
import ticketIdIcon from "@src/assets/svg/ticket-id-icon.svg";
import { useI18N } from "@src/language/language-context";
import cn from "classnames";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import AutoTranslationButton from "@src/components/atoms/auto-translation-button/auto-translation-button";
import { MachineSelectionSection } from "@src/components/molecules/source-selection-section/source-selection-section";
import InitialsAvatar from "@src/components/atoms/initials-avatar/initials-avatar";
import { supportTicketByIdType } from "@src/types/support";
import { getMachineWithSkillsUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-machine-with-skills-use-case";

const cssClassName = "support-chat-modal";

interface SupportChatTicketCardProps {
  ticket: supportTicketByIdType;
  isBot: boolean;
  isMachineSaveDisabled?: boolean;
  onAutoTranslationToggle?: () => void;
  onMachineAssignment?: () => void;
}

export const SupportChatTicketCard = memo(
  ({
    ticket,
    isBot,
    isMachineSaveDisabled = false,
    onAutoTranslationToggle,
    onMachineAssignment,
  }: SupportChatTicketCardProps) => {
    const i18n = useI18N();
    const [isTicketExpanded, setIsTicketExpanded] = useState(true);
    void isBot;

    const texts = {
      issueDetails: i18n("modules.supportChat.issueDetails"),
      machineLabel: i18n("modules.supportChat.sourceLabel"),
      machinePlaceholder: i18n("modules.supportChat.sourcePlaceholder"),
      noMachinesFound: i18n("modules.supportChat.noSourcesFound"),
      saveButton: i18n("modules.supportChat.saveButton"),
    };

    const machineId = ticket.machine?.machine_id ?? null;
    const [resolvedMachineName, setResolvedMachineName] = useState<string>();

    useEffect(() => {
      if (!machineId || ticket.machine?.machine_name) return;
      let cancelled = false;
      getMachineWithSkillsUseCase(machineId).then((result) => {
        if (cancelled) return;
        setResolvedMachineName(
          result.success ? result.data?.name || machineId : machineId,
        );
      });

      return () => {
        cancelled = true;
      };
    }, [machineId, ticket.machine?.machine_name]);

    const machineName = machineId
      ? ticket.machine?.machine_name || resolvedMachineName || machineId
      : null;

    const companyName = ticket?.org_name || null;

    return (
      <div className={styles[`${cssClassName}__ticket-card-panel-details`]}>
        <div className={styles[`${cssClassName}__ticket-card`]}>
          <div className={styles[`${cssClassName}__ticket-title-row`]}>
            {/* <IndicatorText text="" variant={"green"} /> */}
            {machineName ? (
              <div className={styles[`${cssClassName}__machine-title-wrapper`]}>
                <p className={styles[`${cssClassName}__machine-title-label`]}>
                  {texts.machineLabel}
                </p>
                <h3 className={styles[`${cssClassName}__ticket-title`]}>
                  {machineName}
                </h3>
              </div>
            ) : (
              <div
                className={styles[`${cssClassName}__machine-selection-column`]}
              >
                {/* show an empty, selectable dropdown; never pre-fill it from the ticket's
                    machine reference - the name is either on top or unknown */}
                <MachineSelectionSection
                  ticketId={ticket.id}
                  ticketMachineId={null}
                  callbackForRefresh={onMachineAssignment}
                  isSaveDisabled={isMachineSaveDisabled}
                  machineLabel={texts.machineLabel}
                  machinePlaceholder={texts.machinePlaceholder}
                  noMachinesFound={texts.noMachinesFound}
                  confirmButton={texts.saveButton}
                />
              </div>
            )}
            <div className={styles[`${cssClassName}__collapse-button-wrapper`]}>
              <button
                type="button"
                className={cn(
                  styles[`${cssClassName}__collapse-button`],
                  !isTicketExpanded &&
                    styles[`${cssClassName}__collapse-button--collapsed`],
                )}
                onClick={() => setIsTicketExpanded((prev) => !prev)}
              >
                <img
                  src={caretDown}
                  alt="Collapse details"
                  className={cn(
                    styles[`${cssClassName}__collapse-icon`],
                    styles[`${cssClassName}__collapse-icon--top`],
                  )}
                />
                <img
                  src={caretDown}
                  alt="Expand details"
                  className={cn(
                    styles[`${cssClassName}__collapse-icon`],
                    styles[`${cssClassName}__collapse-icon--bottom`],
                  )}
                />
              </button>
            </div>
          </div>

          {isTicketExpanded && (
            <div className={styles[`${cssClassName}__ticket-company`]}>
              <img
                src={companyIcon}
                alt="Company icon"
                className={styles[`${cssClassName}__ticket-company-icon`]}
              />
              <p>{companyName}</p>
            </div>
          )}

          <div className={styles[`${cssClassName}__meta-row`]}>
            <span className={styles[`${cssClassName}__meta-pill`]}>
              <img
                src={ticketIdIcon}
                alt="Ticket ID icon"
                className={styles[`${cssClassName}__meta-pill-icon`]}
              />
              {ticket.id}
            </span>
            <span
              className={cn(
                styles[`${cssClassName}__meta-pill`],
                ticket.status &&
                  styles[
                    `${cssClassName}__meta-pill--${ticket.status.toLowerCase()}`
                  ],
              )}
            >
              {ticket.status ? ticket.status.toUpperCase() : ""}
            </span>
          </div>
          <FlexRowContainer justifyContent="space-between">
            {ticket?.raised_by?.name && (
              <div className={styles[`${cssClassName}__agent-info`]}>
                <InitialsAvatar name={ticket.raised_by.name} />
                <span className={styles[`${cssClassName}__agent-name`]}>
                  {ticket.raised_by.name}
                </span>
              </div>
            )}
            <AutoTranslationButton
              key={ticket.id}
              ticketId={ticket.id}
              disabled={isBot}
              backgroundColor="background"
              ticketAutoTranslationActive={ticket.auto_translate}
              callbackForRefresh={onAutoTranslationToggle}
            />
          </FlexRowContainer>
        </div>
        {isTicketExpanded && (
          <FlexRowContainer justifyContent="space-between">
            <div className={styles[`${cssClassName}__issue-content`]}>
              <h4 className={styles[`${cssClassName}__issue-title`]}>
                {ticket.title}
              </h4>
              <div className={styles[`${cssClassName}__issue-details`]}>
                <p className={styles[`${cssClassName}__issue-details-label`]}>
                  {texts.issueDetails}
                </p>
                <p className={styles[`${cssClassName}__issue-details-text`]}>
                  {ticket.description}
                </p>
              </div>
            </div>
          </FlexRowContainer>
        )}
      </div>
    );
  },
);

SupportChatTicketCard.displayName = "SupportChatTicketCard";
